import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import {
  computeTournamentAnalytics,
  computePlayerBadges,
  computeTeamBadges,
  computeLeaderboard,
} from '../services/gamificationService';
import { getDb } from '../services/store';
import { Award, Sparkles, Trophy } from 'lucide-react';

/**
 * Wrap-up tab — analytics + recap. Visible at all times so commissioners can
 * debrief mid-event; content fills in progressively as games go final.
 */
export function TournamentWrapUpPage() {
  const { tournament, teams } = useActiveTournament();
  useStore((d) => d);

  const analytics = computeTournamentAnalytics(tournament.id);
  const playerBadges = computePlayerBadges(tournament.id);
  const teamBadges = computeTeamBadges(tournament.id);
  const leaderboard = computeLeaderboard(tournament.id);

  const db = getDb();
  const teamById = new Map(teams.map((t) => [t.id, t]));
  const profileNameForPlayer = (playerId: string) => {
    const pl = db.players.find((p) => p.id === playerId);
    if (!pl) return 'Unknown';
    return db.profiles.find((p) => p.id === pl.profileId)?.displayName ?? 'Unknown';
  };

  return (
    <>
      <section className="card card--accent">
        <div>
          <div className="eyebrow">Wrap-up</div>
          <h3 style={{ margin: '4px 0' }}>
            {tournament.status === 'completed'
              ? `${tournament.format === 'season' ? 'Season' : 'Tournament'} recap`
              : analytics.finalGames > 0
                ? 'Live recap (in progress)'
                : 'No games played yet'}
          </h3>
          <p className="muted" style={{ margin: 0 }}>{analytics.recap}</p>
        </div>
      </section>

      <div className="grid grid--4">
        <Stat label="Final" value={analytics.finalGames} />
        <Stat label="Live" value={analytics.inProgress} />
        <Stat label="Upcoming" value={analytics.upcoming} />
        <Stat label="Goals / game" value={analytics.goalsPerGame.toFixed(1)} />
      </div>

      <div className="grid grid--2">
        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title"><Trophy size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Top teams</h3>
              <p className="card__sub">By points so far.</p>
            </div>
          </div>
          {analytics.topTeamsByPoints.length === 0 ? (
            <div className="empty">No standings yet.</div>
          ) : (
            <table className="table">
              <thead><tr><th>#</th><th>Team</th><th>Pts</th></tr></thead>
              <tbody>
                {analytics.topTeamsByPoints.map((t, i) => (
                  <tr key={t.teamId}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{t.teamName}</td>
                    <td>{t.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title"><Sparkles size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Top scorers</h3>
            </div>
          </div>
          {leaderboard.topGoals.length === 0 ? (
            <div className="empty">No goals yet.</div>
          ) : (
            <ol className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {leaderboard.topGoals.slice(0, 6).map((p) => (
                <li key={p.playerId} className="row row--between" style={{ borderBottom: '1px solid var(--surface-line)', padding: '6px 0' }}>
                  <span style={{ fontWeight: 600 }}>{profileNameForPlayer(p.playerId)}</span>
                  <span className="muted">{p.goals}G {p.assists}A · {teamById.get(p.teamId)?.shortCode ?? ''}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <div className="grid grid--2">
        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title"><Award size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Player badges</h3>
            </div>
          </div>
          {playerBadges.length === 0 ? (
            <div className="empty">Badges appear as games finalize.</div>
          ) : (
            <ul className="row" style={{ flexWrap: 'wrap', gap: 6, listStyle: 'none', padding: 0, margin: 0 }}>
              {playerBadges.slice(0, 12).map((b) => (
                <li key={`${b.playerId}-${b.code}`} className="chip chip--info">
                  {b.label}: {profileNameForPlayer(b.playerId)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="card__head">
            <div>
              <h3 className="card__title"><Trophy size={16} style={{ verticalAlign: -2, marginRight: 6 }} /> Team badges</h3>
            </div>
          </div>
          {teamBadges.length === 0 ? (
            <div className="empty">Badges appear as games finalize.</div>
          ) : (
            <ul className="row" style={{ flexWrap: 'wrap', gap: 6, listStyle: 'none', padding: 0, margin: 0 }}>
              {teamBadges.slice(0, 12).map((b) => (
                <li key={`${b.teamId}-${b.code}`} className="chip chip--ok">
                  {b.label}: {teamById.get(b.teamId)?.name ?? ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="card">
      <div className="eyebrow">{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}
